/**
 * Poll Entity Schemas (feed polls, SCH-2238)
 *
 * A poll is a school-scoped entity attached to a host; v1 has one host type, a
 * feed post (R19, KTD1). It has two read shapes (KTD5):
 * - the static definition, carried on the feed item as `object.poll`. It holds
 *   no counts and nothing viewer-specific, so a cached feed page can never show
 *   stale counts;
 * - the live state, read from `GET /v1/polls/:pollId` and returned by every
 *   poll write.
 *
 * Voter identities are never part of either shape; they have their own
 * endpoint (R11).
 */

import { z } from 'zod'

export const POLL_QUESTION_MAX_LENGTH = 200
export const POLL_OPTION_TEXT_MAX_LENGTH = 80
export const POLL_MIN_OPTIONS = 2
export const POLL_MAX_OPTIONS = 10

/** What a poll is attached to. Only feed posts host polls in v1 (R19). */
export const PollHostTypeSchema = z.enum(['POST'])

export type PollHostType = z.infer<typeof PollHostTypeSchema>

/** Trimmed option text, as the poster or a voter types it. */
export const PollOptionTextSchema = z.string().trim().min(1).max(POLL_OPTION_TEXT_MAX_LENGTH)

/**
 * One poll option. `addedByVoter` marks an option a voter added (R8); only
 * those can be removed, by the poster or a school admin (R21).
 */
export const PollOptionSchema = z.object({
  id: z.string(),
  text: z.string(),
  position: z.number().int().nonnegative(),
  addedByVoter: z.boolean()
})

export type PollOption = z.infer<typeof PollOptionSchema>

/** Settings fixed at creation. Polls are never edited in v1 (KTD5). */
export const PollSettingsSchema = z.object({
  allowMultiple: z.boolean(),
  /** Cap on options per voter; `null` means no cap. Only set with `allowMultiple`. */
  maxVotesPerVoter: z.number().int().positive().nullable(),
  /** An anonymous poll never reveals voters, to admins included (R11). */
  isAnonymous: z.boolean(),
  allowVoterOptions: z.boolean(),
  /** ISO timestamp at which the poll closes itself (R13); `null` when it has no closing date. */
  closesAt: z.string().nullable()
})

export type PollSettings = z.infer<typeof PollSettingsSchema>

/**
 * The feed item's `object.poll` (KTD5): id, question, settings and the
 * poster's options. Voter-added options, counts and closed state come only
 * from {@link PollStateSchema}.
 */
export const PollDefinitionSchema = PollSettingsSchema.extend({
  id: z.string(),
  question: z.string(),
  options: z.array(PollOptionSchema)
})

export type PollDefinition = z.infer<typeof PollDefinitionSchema>

/** A live option with its vote count, computed from vote rows (KTD1). */
export const PollOptionStateSchema = PollOptionSchema.extend({
  voteCount: z.number().int().nonnegative()
})

export type PollOptionState = z.infer<typeof PollOptionStateSchema>

/**
 * Live poll state for the viewer (KTD5). Counts are visible before voting
 * (R10). The `can*` flags are the server's answer for this viewer, so clients
 * never re-derive poster-or-admin authority.
 */
export const PollStateSchema = z.object({
  id: z.string(),
  /** Every live option, voter-added ones included, removed ones excluded. */
  options: z.array(PollOptionStateSchema),
  /** Distinct voters, not the sum of option counts (a voter may pick several). */
  voterCount: z.number().int().nonnegative(),
  /** `closedAt` is set, or `closesAt` has passed on the server clock (KTD3). */
  isClosed: z.boolean(),
  /** ISO timestamp of a manual close; `null` when open or closed only by `closesAt`. */
  closedAt: z.string().nullable(),
  closesAt: z.string().nullable(),
  /** Option ids the viewer currently votes for; empty when they have not voted. */
  myOptionIds: z.array(z.string()),
  canClose: z.boolean(),
  /** Always false on an anonymous poll (R11). */
  canSeeVoters: z.boolean(),
  /** Whether the viewer may remove voter-added options (R21). */
  canRemoveOption: z.boolean()
})

export type PollState = z.infer<typeof PollStateSchema>

/**
 * The `poll` field of a create-post payload (R3). Semantic checks that need
 * the surrounding post (content type, publish-now) live in
 * `refinePollPostFields` in `api/content`.
 */
export const PollCreateSchema = z
  .object({
    question: z.string().trim().min(1).max(POLL_QUESTION_MAX_LENGTH),
    options: z.array(PollOptionTextSchema).min(POLL_MIN_OPTIONS).max(POLL_MAX_OPTIONS),
    allowMultiple: z.boolean(),
    maxVotesPerVoter: z.number().int().nullable().optional(),
    isAnonymous: z.boolean(),
    allowVoterOptions: z.boolean(),
    /** ISO timestamp, in the future when sent. */
    closesAt: z.string().datetime({ offset: true }).nullable().optional()
  })
  .superRefine((poll, ctx) => {
    const seen = new Set<string>()
    poll.options.forEach((text, index) => {
      const key = text.toLowerCase()
      if (seen.has(key)) {
        ctx.addIssue({ code: 'custom', path: ['options', index], message: 'Poll options must be unique' })
      }
      seen.add(key)
    })

    if (poll.maxVotesPerVoter != null) {
      if (!poll.allowMultiple) {
        ctx.addIssue({
          code: 'custom',
          path: ['maxVotesPerVoter'],
          message: 'maxVotesPerVoter can only be set on a multiple-choice poll'
        })
      } else if (poll.maxVotesPerVoter < 2 || poll.maxVotesPerVoter > poll.options.length) {
        ctx.addIssue({
          code: 'custom',
          path: ['maxVotesPerVoter'],
          message: 'maxVotesPerVoter must be between 2 and the number of options'
        })
      }
    }

    if (poll.closesAt != null && new Date(poll.closesAt).getTime() <= Date.now()) {
      ctx.addIssue({ code: 'custom', path: ['closesAt'], message: 'closesAt must be in the future' })
    }
  })

export type PollCreate = z.infer<typeof PollCreateSchema>
